<script lang="ts" setup>
import {
  ref,
  computed,
  onMounted,
  onUnmounted,
  nextTick,
  watch,
  type PropType,
} from 'vue';
import { storeToRefs } from 'pinia';
import PerfilOficialSwiperHeader from '@/components/perfil-oficial/SwiperHeader.vue';

// Cards components (VipCards específicos para TabsVIP)
import MissionCard from '@/components/smartico/apostatudo/vipcards/MissionCard.vue';
import TournamentCard from '@/components/smartico/apostatudo/vipcards/TournamentCard.vue';
import MinigameCard from '@/components/smartico/apostatudo/vipcards/MinigameCard.vue';
import StoreCard from '@/components/smartico/apostatudo/vipcards/StoreCard.vue';
import VipPurchaseResult from '@/components/vip/modal/PurchaseResult.vue';

interface TabDefinition {
  id: string;
  title: string;
  icon: string;
  seeMoreUrl: string;
}

const props = defineProps({
  headerTitle: {
    type: String,
    default: 'Área VIP',
  },
  headerIconPath: {
    type: String,
    default: 'home/vip',
  },
  headerSeeMoreUrl: {
    type: String,
    default: '/vip',
  },
  smarticoTournamentFilter: {
    type: Array as PropType<string[]>,
    default: undefined,
  },
  smarticoMissionFilter: {
    type: Array as PropType<string[]>,
    default: undefined,
  },
  smarticoMinigameFilter: {
    type: [String, Array] as PropType<string | string[]>,
    default: undefined,
  },
  smarticoStoreFilter: {
    type: Array as PropType<string[]>,
    default: undefined,
  },
});

// Stores
const missionsStore = useSmarticoMissions();
const tournamentsStore = useSmarticoTournaments();
const miniGamesStore = useSmarticoMiniGames();
const shopStore = useSmarticoShop();

// Auth
const { isAuthenticated } = storeToRefs(useAuthStore());
const { openLogin } = useAuth();
const { loadUserInfosProfile } = useSmarticoUserInfos();
const { t } = useTexts();

// Store modal state
const selectedItem = ref<TStoreItem>();
const openModal = ref<boolean>(false);
const isLoadingBuyItem = ref(false);
const purchaseResultOpen = ref(false);
const purchaseResultType = ref<'success' | 'error' | 'incomplete'>('success');
const purchaseResultText = ref('');

function getCurrencyIcon(pt: any) {
  const type = String(pt || '').toLowerCase();

  if (type === 'diamonds') return 'currency-header/diamond';
  if (type === 'gems') return 'currency-header/gem';

  return 'currency-header/coin';
}

function getCurrencyLabel(pt: any) {
  const type = String(pt || '')
    .toLowerCase()
    .trim();

  if (type === 'gems') return 'GEMS';
  if (type === 'diamonds') return 'DIAMONDS';

  return 'COINS';
}

function openItemStoreDetails(item: TStoreItem) {
  if (!isAuthenticated || !isAuthenticated.value) {
    try {
      openLogin();
    } catch (e) {}

    return;
  }
  selectedItem.value = item;
  openModal.value = true;
}

async function buyItemAction() {
  if (!selectedItem.value?.id) return;

  isLoadingBuyItem.value = true;

  try {
    const result = await shopStore.buyShopItem(selectedItem.value.id);

    openModal.value = false;

    if (!result || (result?.err_code && result.err_code !== 0)) {
      const message =
        result?.err_message || t('gamification.store.fail_to_buy');

      purchaseResultType.value = 'error';
      purchaseResultText.value = String(message);
    } else {
      const type = selectedItem.value.type;
      const message = result?.message
        ? result.message
        : !type
          ? t('gamification.store.extended_purchase_successfully')
          : t('gamification.store.purchase_successfully');

      purchaseResultType.value = 'success';
      purchaseResultText.value = String(message);
    }
    purchaseResultOpen.value = true;

    // Refresh data
    await shopStore.loadshopItems({ force: true });
    await loadUserInfosProfile();
  } catch (e) {
    openModal.value = false;
    purchaseResultType.value = 'error';
    purchaseResultText.value = String(t('gamification.store.fail_to_buy'));
    purchaseResultOpen.value = true;
  } finally {
    isLoadingBuyItem.value = false;
  }
}

// Tabs definition
const tabs: TabDefinition[] = [
  {
    id: 'tournaments',
    title: 'Torneios',
    icon: 'tournament/section/disponiveis',
    seeMoreUrl: '/vip/tournaments',
  },
  {
    id: 'missions',
    title: 'Missões',
    icon: 'mission/gift',
    seeMoreUrl: '/vip/missions',
  },
  {
    id: 'minigames',
    title: 'Minigames',
    icon: 'mini-games/girar',
    seeMoreUrl: '/vip/mini-games',
  },
  {
    id: 'store',
    title: 'Loja',
    icon: 'currency-header/coin',
    seeMoreUrl: '/vip/store',
  },
];

const activeTab = ref(0);

// Refs para drag-to-scroll
const tabsNavigation = ref<HTMLElement | null>(null);
const cardsContainer = ref<HTMLElement | null>(null);
const isDragging = ref(false);
const isDraggingCards = ref(false);
const startX = ref(0);
const scrollLeft = ref(0);
const startXCards = ref(0);
const scrollLeftCards = ref(0);
const dragged = ref(false);

// Scroll metrics para header navigation
const currentCardsScrollLeft = ref(0);
const currentCardsScrollWidth = ref(0);
const currentCardsClientWidth = ref(0);

// Lazy loading: quantidade de itens visíveis por tab
const INITIAL_ITEMS = 6;
const LOAD_MORE_ITEMS = 10;

const visibleCounts = ref({
  missions: INITIAL_ITEMS,
  tournaments: INITIAL_ITEMS,
  minigames: INITIAL_ITEMS,
  store: INITIAL_ITEMS,
});

// Reset visible counts when changing tabs
watch(activeTab, () => {
  // Reseta o scroll do container
  if (cardsContainer.value) {
    cardsContainer.value.scrollLeft = 0;
  }
});

// ====== HELPERS DE FILTRO (custom_data) ======

function normalizeFilters(filters: string[] | string | undefined): string[] {
  if (!filters) return [];

  if (Array.isArray(filters)) {
    return filters
      .filter((f) => typeof f === 'string' && f !== '')
      .map((f) => f.toUpperCase());
  }

  const str = String(filters).trim();

  return str ? [str.toUpperCase()] : [];
}

function normalizeCustomData(customData: any): string[] {
  if (!customData) return [];
  if (Array.isArray(customData)) return customData.map(String);
  if (typeof customData === 'object') {
    return Object.values(customData).map(String);
  }

  return String(customData)
    .split(',')
    .map((item) => item.trim());
}

function matchesCustomDataFilter(
  customData: any,
  normalizedFilters: string[],
): boolean {
  // Sem filtros → mostra tudo
  if (normalizedFilters.length === 0) return true;

  const dataItems = normalizeCustomData(customData);

  if (dataItems.length === 0) return false;

  return normalizedFilters.some((filter) =>
    dataItems.some((item) => item.toUpperCase() === filter),
  );
}

// ====== ORDENAÇÃO E FILTROS ======

// Minigames: ordenar por tipo de jogo e priority
const gameTypeOrder = [
  'wheel',
  'giftbox',
  'scratch',
  'coin_flip',
  'matchx',
  'quiz',
];

function sortMiniGamesByTypeAndPriority(
  miniGames: TMiniGameTemplate[],
): TMiniGameTemplate[] {
  return [...miniGames]
    .filter((mg) => mg.custom_section_id !== 717) // Exclui seção customizada
    .filter(
      (mg) =>
        !(
          mg.spin_count === 0 &&
          mg.saw_template_ui_definition?.disable_background_music === true
        ),
    )
    .sort((a, b) => {
      // Primeiro por tipo de jogo
      const typeA = a.saw_game_type || 'other';
      const typeB = b.saw_game_type || 'other';
      const orderA = gameTypeOrder.indexOf(typeA);
      const orderB = gameTypeOrder.indexOf(typeB);

      if (orderA !== -1 && orderB !== -1 && orderA !== orderB) {
        return orderA - orderB;
      }
      if (orderA !== -1 && orderB === -1) return -1;
      if (orderA === -1 && orderB !== -1) return 1;

      // Depois por priority
      const priorityA = a.saw_template_ui_definition?.priority ?? 10;
      const priorityB = b.saw_template_ui_definition?.priority ?? 10;

      return priorityA - priorityB;
    });
}

// Loja: ordenar por moeda (combos → coins → diamonds → gems) e priority
const COMBOS_CATEGORY_ID = 488;
const currencyOrder = ['combos', 'points', 'diamonds', 'gems'];

function getItemCurrency(item: TStoreItem): string {
  const categories = (item as any)?.category_ids;

  if (Array.isArray(categories) && categories.includes(COMBOS_CATEGORY_ID)) {
    return 'combos';
  }

  const pt = String((item as any)?.purchase_type || '').toLowerCase();

  if (pt === 'diamonds') return 'diamonds';
  if (pt === 'gems') return 'gems';

  return 'points';
}

function sortStoreItemsByCurrencyAndPriority(
  items: TStoreItem[],
): TStoreItem[] {
  return [...items].sort((a, b) => {
    // Primeiro por tipo de moeda
    const currA = getItemCurrency(a);
    const currB = getItemCurrency(b);
    const orderA = currencyOrder.indexOf(currA);
    const orderB = currencyOrder.indexOf(currB);

    if (orderA !== orderB) return orderA - orderB;

    // Depois por priority
    const priorityA = (a as any)?.priority ?? Number.MAX_SAFE_INTEGER;
    const priorityB = (b as any)?.priority ?? Number.MAX_SAFE_INTEGER;

    if (priorityA !== priorityB) return priorityA - priorityB;

    // Fallback por id
    return (a.id || 0) - (b.id || 0);
  });
}

// ====== COMPUTED DATA COM ORDENAÇÃO E LAZY LOADING ======

// Todas as missões ordenadas (concatena todos os status na ordem correta)
const allMissionsSorted = computed(() => {
  const claimable = missionsStore.claimableMissions || [];
  const optIn = missionsStore.optInMissions || [];
  const participating = missionsStore.alreadyParticipatingMissions || [];

  let allMissions: TMissionOrBadge[];

  // Se usuário deslogado, "participating" aparece junto com "optIn" (como disponíveis)
  if (!isAuthenticated.value) {
    // Junta optIn + participating e ordena por position
    const available = [...optIn, ...participating].sort((a, b) => {
      const posA = a.position || 0;
      const posB = b.position || 0;

      if (!posA && !posB) return 0;
      if (!posA) return 1;
      if (!posB) return -1;

      return posA - posB;
    });

    allMissions = [...claimable, ...available];
  } else {
    // Usuário logado: mantém a ordem claimable → optIn → participating
    allMissions = [...claimable, ...optIn, ...participating];
  }

  // Aplica filtro por custom_data se props.smarticoMissionFilter foi informado
  const missionFilters = normalizeFilters(props.smarticoMissionFilter);

  if (missionFilters.length === 0) return allMissions;

  return allMissions.filter((mission) =>
    matchesCustomDataFilter(mission.custom_data, missionFilters),
  );
});

const missionsData = computed(() => {
  return allMissionsSorted.value.slice(0, visibleCounts.value.missions);
});

// Torneios ativos (não finalizados) - ordenados por priority
const allActiveTournaments = computed(() => {
  const all = tournamentsStore.allTournaments || [];
  const tournamentFilters = normalizeFilters(props.smarticoTournamentFilter);

  let filtered = all.filter((t) => !t.is_finished);

  // Aplica filtro por custom_data se props.smarticoTournamentFilter foi informado
  if (tournamentFilters.length > 0) {
    if (tournamentFilters.includes('NUMERIC')) {
      // Filtro NUMERIC: mostra torneios com custom_data numérico
      filtered = filtered.filter((tournament) => {
        const customData = normalizeCustomData((tournament as any).custom_data);

        return customData.some((item) => !isNaN(Number(item)));
      });
    } else {
      filtered = filtered.filter((tournament) =>
        matchesCustomDataFilter(
          (tournament as any).custom_data,
          tournamentFilters,
        ),
      );
    }
  }

  return filtered.sort((a, b) => (a as any).priority - (b as any).priority);
});

const tournamentsData = computed(() => {
  return allActiveTournaments.value.slice(0, visibleCounts.value.tournaments);
});

// Todos os minigames ordenados
const allMiniGamesSorted = computed(() => {
  const sorted = sortMiniGamesByTypeAndPriority(miniGamesStore.miniGames || []);

  // Aplica filtro por custom_section_id se props.smarticoMinigameFilter foi informado
  const minigameFilters = normalizeFilters(
    Array.isArray(props.smarticoMinigameFilter)
      ? props.smarticoMinigameFilter
      : props.smarticoMinigameFilter
        ? [props.smarticoMinigameFilter]
        : undefined,
  );

  if (minigameFilters.length === 0) return sorted;

  return sorted.filter((mg) => {
    const customSectionId = mg.custom_section_id;

    if (!customSectionId) return false;

    return minigameFilters.some(
      (filter) =>
        String(customSectionId).toUpperCase() === filter.toUpperCase(),
    );
  });
});

const miniGamesData = computed(() => {
  return allMiniGamesSorted.value.slice(0, visibleCounts.value.minigames);
});

// Todos os itens da loja ordenados e filtrados
const allStoreItemsSorted = computed(() => {
  const sorted = sortStoreItemsByCurrencyAndPriority(shopStore.shopItems || []);
  const storeFilters = normalizeFilters(props.smarticoStoreFilter);

  if (storeFilters.length === 0) return sorted;

  return sorted.filter((item) =>
    matchesCustomDataFilter((item as any).custom_data, storeFilters),
  );
});

const storeData = computed(() => {
  return allStoreItemsSorted.value.slice(0, visibleCounts.value.store);
});

// Check if there are more items to load
const hasMoreItems = computed(() => {
  const tabId = visibleTabs.value[activeTab.value]?.id;

  switch (tabId) {
    case 'missions':
      return allMissionsSorted.value.length > visibleCounts.value.missions;

    case 'tournaments':
      return (
        allActiveTournaments.value.length > visibleCounts.value.tournaments
      );

    case 'minigames':
      return allMiniGamesSorted.value.length > visibleCounts.value.minigames;

    case 'store':
      return allStoreItemsSorted.value.length > visibleCounts.value.store;

    default:
      return false;
  }
});

// Load more items when scrolling near the end
function checkAndLoadMore() {
  if (!cardsContainer.value || !hasMoreItems.value) return;

  const { scrollLeft, scrollWidth, clientWidth } = cardsContainer.value;
  const scrollThreshold = 200; // pixels before end to trigger load

  if (scrollLeft + clientWidth >= scrollWidth - scrollThreshold) {
    loadMoreItems();
  }
}

function loadMoreItems() {
  const tabId = visibleTabs.value[activeTab.value]?.id;

  switch (tabId) {
    case 'missions':
      visibleCounts.value.missions += LOAD_MORE_ITEMS;
      break;

    case 'tournaments':
      visibleCounts.value.tournaments += LOAD_MORE_ITEMS;
      break;

    case 'minigames':
      visibleCounts.value.minigames += LOAD_MORE_ITEMS;
      break;

    case 'store':
      visibleCounts.value.store += LOAD_MORE_ITEMS;
      break;
  }
}

// Load data when Smartico is ready
const { isReady } = useSmarticoUserReady();

// Loading state per tab - includes waiting for Smartico to be ready
const isMissionsLoading = computed(
  () => !isReady.value || missionsStore.isLoading,
);

const isTournamentsLoading = computed(
  () => !isReady.value || tournamentsStore.isLoading,
);

const isMiniGamesLoading = computed(
  () => !isReady.value || miniGamesStore.isLoading,
);

const isStoreLoading = computed(() => !isReady.value || shopStore.isLoading);

// Helper: retorna todos os dados (sem limit) de uma tab pelo id
function getAllDataForTab(tabId: string) {
  switch (tabId) {
    case 'missions':
      return allMissionsSorted.value;

    case 'tournaments':
      return allActiveTournaments.value;

    case 'minigames':
      return allMiniGamesSorted.value;

    case 'store':
      return allStoreItemsSorted.value;

    default:
      return [];
  }
}

// Helper: verifica se uma tab ainda está carregando
function isTabLoading(tabId: string): boolean {
  switch (tabId) {
    case 'missions':
      return isMissionsLoading.value;

    case 'tournaments':
      return isTournamentsLoading.value;

    case 'minigames':
      return isMiniGamesLoading.value;

    case 'store':
      return isStoreLoading.value;

    default:
      return false;
  }
}

// Tabs visíveis: só exibe tabs que possuem dados ou ainda estão carregando
const visibleTabs = computed(() => {
  return tabs.filter((tab) => {
    // Se ainda está carregando, mantém visível (skeleton)
    if (isTabLoading(tab.id)) return true;

    // Se já carregou, só mostra se tiver dados
    return getAllDataForTab(tab.id).length > 0;
  });
});

// Se o componente inteiro deve ser exibido
const hasAnyContent = computed(() => visibleTabs.value.length > 0);

// Current tab data
const currentTabData = computed(() => {
  const tabId = visibleTabs.value[activeTab.value]?.id;

  switch (tabId) {
    case 'missions':
      return missionsData.value;

    case 'tournaments':
      return tournamentsData.value;

    case 'minigames':
      return miniGamesData.value;

    case 'store':
      return storeData.value;

    default:
      return [];
  }
});

// Check if current tab has data
const hasData = computed(() => currentTabData.value.length > 0);

// Current tab loading state
const isCurrentTabLoading = computed(() => {
  const tabId = visibleTabs.value[activeTab.value]?.id;

  return tabId ? isTabLoading(tabId) : false;
});

// Skeleton count for loading state
const skeletonCount = 6;

// Current see more URL based on active tab
const currentSeeMoreUrl = computed(() => {
  return (
    visibleTabs.value[activeTab.value]?.seeMoreUrl || props.headerSeeMoreUrl
  );
});

// Garante que activeTab não aponte para um índice inválido ao reduzir tabs
watch(visibleTabs, (newTabs) => {
  if (activeTab.value >= newTabs.length) {
    activeTab.value = Math.max(0, newTabs.length - 1);
  }
});

watch(
  isReady,
  async (ready) => {
    if (ready) {
      await Promise.all([
        missionsStore.loadMissions(),
        tournamentsStore.loadTournaments(),
        miniGamesStore.loadMiniGames(),
        shopStore.loadshopItems(),
      ]);
    }
  },
  { immediate: true },
);

// Tab selection
function selectTab(index: number) {
  if (dragged.value) {
    dragged.value = false;

    return;
  }
  if (activeTab.value === index) {
    return;
  }
  activeTab.value = index;
  // Reset scroll position when changing tabs
  if (cardsContainer.value) {
    cardsContainer.value.scrollLeft = 0;
  }
  nextTick(updateCardsScrollMetrics);
}

// Scroll metrics update
function updateCardsScrollMetrics() {
  if (cardsContainer.value) {
    currentCardsScrollLeft.value = cardsContainer.value.scrollLeft;
    currentCardsScrollWidth.value = cardsContainer.value.scrollWidth;
    currentCardsClientWidth.value = cardsContainer.value.clientWidth;

    // Check if we need to load more items
    checkAndLoadMore();
  }
}

// Scroll controls for header
function scrollNextCards() {
  if (cardsContainer.value) {
    const scrollAmount = cardsContainer.value.clientWidth * 0.6;

    cardsContainer.value.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  }
}

function scrollPrevCards() {
  if (cardsContainer.value) {
    const scrollAmount = cardsContainer.value.clientWidth * 0.6;

    cardsContainer.value.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
  }
}

const swiperControls = {
  scrollNext: scrollNextCards,
  scrollPrev: scrollPrevCards,
  currentScrollLeft: currentCardsScrollLeft,
  currentScrollWidth: currentCardsScrollWidth,
  currentClientWidth: currentCardsClientWidth,
};

// Drag handlers for tabs
function onMouseDownTabs(e: MouseEvent) {
  if (!tabsNavigation.value) return;
  isDragging.value = true;
  startX.value = e.pageX - tabsNavigation.value.offsetLeft;
  scrollLeft.value = tabsNavigation.value.scrollLeft;
  tabsNavigation.value.style.cursor = 'grabbing';
  tabsNavigation.value.style.userSelect = 'none';
  dragged.value = false;
}

function onMouseMoveTabs(e: MouseEvent) {
  if (!isDragging.value || !tabsNavigation.value) return;
  e.preventDefault();

  const x = e.pageX - tabsNavigation.value.offsetLeft;
  const walk = x - startX.value;

  if (Math.abs(walk) > 5) {
    dragged.value = true;
  }
  tabsNavigation.value.scrollLeft = scrollLeft.value - walk;
}

function onMouseUpTabs() {
  if (!tabsNavigation.value) return;
  isDragging.value = false;
  tabsNavigation.value.style.cursor = 'grab';
  tabsNavigation.value.style.userSelect = 'auto';
}

// Drag handlers for cards
function onMouseDownCards(e: MouseEvent) {
  if (!cardsContainer.value) return;
  isDraggingCards.value = true;
  startXCards.value = e.pageX - cardsContainer.value.offsetLeft;
  scrollLeftCards.value = cardsContainer.value.scrollLeft;
  cardsContainer.value.style.cursor = 'grabbing';
  cardsContainer.value.style.userSelect = 'none';
  dragged.value = false;
}

function onMouseMoveCards(e: MouseEvent) {
  if (!isDraggingCards.value || !cardsContainer.value) return;
  e.preventDefault();

  const x = e.pageX - cardsContainer.value.offsetLeft;
  const walk = x - startXCards.value;

  if (Math.abs(walk) > 5) {
    dragged.value = true;
  }
  cardsContainer.value.scrollLeft = scrollLeftCards.value - walk;
  updateCardsScrollMetrics();
}

function onMouseUpCards() {
  if (!cardsContainer.value) return;
  isDraggingCards.value = false;
  cardsContainer.value.style.cursor = 'grab';
  cardsContainer.value.style.userSelect = 'auto';
  if (dragged.value) {
    setTimeout(() => {
      dragged.value = false;
    }, 50);
  }
}

function onMouseLeaveCards() {
  if (!cardsContainer.value) return;
  isDraggingCards.value = false;
  cardsContainer.value.style.cursor = 'grab';
  cardsContainer.value.style.userSelect = 'auto';
}

// Handle card click prevention during drag
function handleCardClick(event: MouseEvent) {
  if (dragged.value) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    dragged.value = false;
  }
}

// Handle store item selection
function handleStoreItemSelect(item: TStoreItem) {
  if (dragged.value) return;
  openItemStoreDetails(item);
}

// Handle mission opt-in
function handleMissionOptedIn() {
  // Refresh missions after opt-in
  missionsStore.loadMissions();
}

onMounted(() => {
  nextTick(() => {
    updateCardsScrollMetrics();
  });
  cardsContainer.value?.addEventListener('scroll', updateCardsScrollMetrics);
  window.addEventListener('resize', updateCardsScrollMetrics);
});

onUnmounted(() => {
  cardsContainer.value?.removeEventListener('scroll', updateCardsScrollMetrics);
  window.removeEventListener('resize', updateCardsScrollMetrics);
});
</script>

<template>
  <div
    v-if="hasAnyContent"
    class="tabsVIP"
  >
    <PerfilOficialSwiperHeader
      :icon-path="headerIconPath"
      :see-more-url="currentSeeMoreUrl"
      :show-navigation="false"
      :swiper-ref="swiperControls"
      :title="headerTitle"
    />

    <!-- Tabs Navigation -->
    <div
      ref="tabsNavigation"
      class="tabsVIP__tabs-navigation"
      @mousedown="onMouseDownTabs"
      @mousemove="onMouseMoveTabs"
      @mouseup="onMouseUpTabs"
    >
      <BaseFilterTabButton
        v-for="(tab, index) in visibleTabs"
        :key="tab.id"
        :active="activeTab === index"
        :label="tab.title"
        @click="selectTab(index)"
      />
    </div>

    <!-- Cards Container -->
    <div
      ref="cardsContainer"
      class="tabsVIP__content"
      :data-tab="visibleTabs[activeTab]?.id"
      @mousedown="onMouseDownCards"
      @mouseleave="onMouseLeaveCards"
      @mousemove="onMouseMoveCards"
      @mouseup="onMouseUpCards"
    >
      <!-- Loading State - Missions Skeleton -->
      <div
        v-if="
          isCurrentTabLoading &&
          !hasData &&
          visibleTabs[activeTab]?.id === 'missions'
        "
        class="tabsVIP__cards-grid"
        data-tab="missions"
      >
        <div
          v-for="n in skeletonCount"
          :key="`skeleton-${n}`"
          class="tabsVIP__card-wrapper tabsVIP__card-wrapper--missions"
        >
          <div class="tabsVIP__skeleton-mission">
            <!-- Header: Imagem + Info -->
            <div class="tabsVIP__skeleton-mission-header">
              <div class="tabsVIP__skeleton-mission-image" />
              <div class="tabsVIP__skeleton-mission-info">
                <div class="tabsVIP__skeleton-mission-title" />
                <div class="tabsVIP__skeleton-mission-title2" />
                <div class="tabsVIP__skeleton-mission-desc" />
                <div class="tabsVIP__skeleton-mission-desc2" />
              </div>
            </div>
            <!-- Prêmio -->
            <div class="tabsVIP__skeleton-mission-reward" />
            <!-- Botão -->
            <div class="tabsVIP__skeleton-mission-btn" />
          </div>
        </div>
      </div>

      <!-- Loading State - Tournaments Skeleton -->
      <div
        v-else-if="
          isCurrentTabLoading &&
          !hasData &&
          visibleTabs[activeTab]?.id === 'tournaments'
        "
        class="tabsVIP__cards-grid"
        data-tab="tournaments"
      >
        <div
          v-for="n in skeletonCount"
          :key="`skeleton-tournament-${n}`"
          class="tabsVIP__card-wrapper tabsVIP__card-wrapper--tournaments"
        >
          <div class="tabsVIP__skeleton-tournament">
            <!-- Imagem -->
            <div class="tabsVIP__skeleton-tournament-image" />
            <!-- Título -->
            <div class="tabsVIP__skeleton-tournament-title" />
            <!-- Prêmios -->
            <div class="tabsVIP__skeleton-tournament-prizes">
              <div class="tabsVIP__skeleton-tournament-prize" />
              <div class="tabsVIP__skeleton-tournament-prize" />
              <div class="tabsVIP__skeleton-tournament-prize" />
            </div>
            <!-- Footer -->
            <div class="tabsVIP__skeleton-tournament-footer">
              <div class="tabsVIP__skeleton-tournament-total" />
              <div class="tabsVIP__skeleton-tournament-btn" />
            </div>
          </div>
        </div>
      </div>

      <!-- Loading State - Minigames Skeleton -->
      <div
        v-else-if="
          isCurrentTabLoading &&
          !hasData &&
          visibleTabs[activeTab]?.id === 'minigames'
        "
        class="tabsVIP__cards-grid"
        data-tab="minigames"
      >
        <div
          v-for="n in skeletonCount"
          :key="`skeleton-minigame-${n}`"
          class="tabsVIP__card-wrapper tabsVIP__card-wrapper--minigames"
        >
          <div class="tabsVIP__skeleton-minigame">
            <!-- Header -->
            <div class="tabsVIP__skeleton-minigame-header">
              <div class="tabsVIP__skeleton-minigame-image" />
              <div class="tabsVIP__skeleton-minigame-info">
                <div class="tabsVIP__skeleton-minigame-title" />
                <div class="tabsVIP__skeleton-minigame-title2" />
                <div class="tabsVIP__skeleton-minigame-desc" />
                <div class="tabsVIP__skeleton-minigame-desc2" />
              </div>
            </div>
            <!-- Preço -->
            <div class="tabsVIP__skeleton-minigame-price" />
            <!-- Botão -->
            <div class="tabsVIP__skeleton-minigame-btn" />
          </div>
        </div>
      </div>

      <!-- Loading State - Store Skeleton -->
      <div
        v-else-if="
          isCurrentTabLoading &&
          !hasData &&
          visibleTabs[activeTab]?.id === 'store'
        "
        class="tabsVIP__cards-grid"
        data-tab="store"
      >
        <div
          v-for="n in skeletonCount"
          :key="`skeleton-store-${n}`"
          class="tabsVIP__card-wrapper tabsVIP__card-wrapper--store"
        >
          <div class="tabsVIP__skeleton-store">
            <!-- Header -->
            <div class="tabsVIP__skeleton-store-header">
              <div class="tabsVIP__skeleton-store-image" />
              <div class="tabsVIP__skeleton-store-info">
                <div class="tabsVIP__skeleton-store-title" />
                <div class="tabsVIP__skeleton-store-title2" />
                <div class="tabsVIP__skeleton-store-desc" />
                <div class="tabsVIP__skeleton-store-desc2" />
              </div>
            </div>
            <!-- Preço -->
            <div class="tabsVIP__skeleton-store-price" />
            <!-- Botão -->
            <div class="tabsVIP__skeleton-store-btn" />
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div
        v-else-if="!hasData"
        class="tabsVIP__empty"
      >
        <span>Nenhum item disponível</span>
      </div>

      <!-- Cards Grid -->
      <div
        v-else
        class="tabsVIP__cards-grid"
        :data-tab="visibleTabs[activeTab]?.id"
      >
        <!-- Missions Tab -->
        <template v-if="visibleTabs[activeTab]?.id === 'missions'">
          <div
            v-for="mission in missionsData"
            :key="mission.id"
            class="tabsVIP__card-wrapper tabsVIP__card-wrapper--missions"
            @click="handleCardClick"
          >
            <MissionCard
              :mission="mission"
              @mission-opted-in="handleMissionOptedIn"
            />
          </div>
        </template>

        <!-- Tournaments Tab -->
        <template v-else-if="visibleTabs[activeTab]?.id === 'tournaments'">
          <div
            v-for="tournament in tournamentsData"
            :key="tournament.instance_id"
            class="tabsVIP__card-wrapper tabsVIP__card-wrapper--tournaments"
            @click="handleCardClick"
          >
            <TournamentCard :tournament="tournament" />
          </div>
        </template>

        <!-- Minigames Tab -->
        <template v-else-if="visibleTabs[activeTab]?.id === 'minigames'">
          <div
            v-for="minigame in miniGamesData"
            :key="minigame.id"
            class="tabsVIP__card-wrapper tabsVIP__card-wrapper--minigames"
            @click="handleCardClick"
          >
            <MinigameCard :minigame="minigame" />
          </div>
        </template>

        <!-- Store Tab -->
        <template v-else-if="visibleTabs[activeTab]?.id === 'store'">
          <div
            v-for="item in storeData"
            :key="item.id"
            class="tabsVIP__card-wrapper tabsVIP__card-wrapper--store"
            @click="handleCardClick"
          >
            <StoreCard
              :item="item"
              @select-item="handleStoreItemSelect"
            />
          </div>
        </template>
      </div>
    </div>
  </div>

  <!-- Modal de compra da loja -->
  <BaseModalMissionModal
    v-model="openModal"
    :z-index="50"
  >
    <template #header>
      <NuxtIcon
        class="text-xl text-white"
        :filled="true"
        name="store/cart"
      />
      <p class="text-lg font-bold text-white">Resumo da compra</p>
    </template>
    <div
      v-if="selectedItem"
      class="w-full inline-flex flex-col justify-start items-start gap-3.5"
    >
      <BaseLoading
        class="!bg-auth-bg-primary"
        :show="isLoadingBuyItem"
      />

      <div
        class="self-stretch flex flex-col justify-center items-start gap-2.5 overflow-hidden"
      >
        <div
          class="self-stretch flex flex-col justify-start items-start gap-2.5"
        >
          <div
            class="self-stretch p-3.5 sm:p-5 bg-gray-950 rounded-[10px] border-[1.5px] border-solid border-white/10 backdrop-blur-[10px] flex flex-col sm:flex-row justify-center sm:justify-between items-start sm:items-center gap-2.5 sm:gap-4"
          >
            <div
              class="self-stretch sm:flex-1 rounded-[10px] inline-flex justify-center items-center gap-2.5"
            >
              <img
                :alt="selectedItem.name"
                class="w-14 h-14 rounded-[6px] object-cover flex-shrink-0"
                :src="selectedItem.image"
              />
              <div
                class="flex-1 inline-flex flex-col justify-start items-start gap-0.5 min-w-0"
              >
                <div
                  class="self-stretch justify-center text-white font-semibold leading-5 line-clamp-2"
                  style="font-size: 15px"
                  :title="selectedItem.name"
                >
                  {{ selectedItem.name }}
                </div>
                <div
                  class="self-stretch justify-center text-white/50 text-sm font-medium leading-4 line-clamp-3"
                >
                  {{ selectedItem.description }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Custo total -->
        <div
          class="self-stretch py-2.5 bg-gray-950 rounded-[10px] backdrop-blur-[10px] inline-flex flex-row justify-center items-center gap-2 sm:gap-3.5"
        >
          <NuxtIcon
            class="text-[28px] flex-shrink-0"
            :filled="true"
            :name="getCurrencyIcon(selectedItem?.purchase_type)"
          />
          <div
            class="text-center justify-start text-[#00c9ff] text-base font-extrabold leading-tight"
          >
            {{
              (
                (selectedItem.purchase_points_amount ?? selectedItem.price) ||
                0
              ).toLocaleString('pt-BR')
            }}
            {{ getCurrencyLabel(selectedItem?.purchase_type) }}
          </div>
        </div>

        <!-- Botão concluir -->
        <button
          class="self-stretch h-12 px-2.5 rounded-md outline outline-1 outline-offset-[-1px] outline-red-300 inline-flex justify-center items-center gap-1.5 [background:radial-gradient(ellipse_138%_168.3%_at_36.76%_-16.67%,#FF704A_0%,#FF3400_100%)] hover:brightness-90 transition disabled:opacity-60 disabled:cursor-not-allowed"
          :disabled="!selectedItem.can_buy || isLoadingBuyItem"
          @click="buyItemAction"
        >
          <span class="text-white text-base font-semibold leading-3">
            CONCLUIR COMPRA
          </span>
          <NuxtIcon
            class="text-white text-[18px]"
            :filled="true"
            name="store/cart"
          />
        </button>

        <!-- Limite / alerta (quando aplicável) -->
        <div
          v-if="!selectedItem.can_buy"
          class="self-stretch mt-1"
        >
          <div class="alert alert-warning">
            {{ selectedItem.limit_message }}
          </div>
        </div>
      </div>
    </div>
  </BaseModalMissionModal>

  <!-- Resultado da compra -->
  <Teleport to="body">
    <div
      v-if="purchaseResultOpen"
      class="fixed inset-0 z-[999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      @click.self="purchaseResultOpen = false"
      @keydown.esc="purchaseResultOpen = false"
    >
      <transition
        enter-active-class="duration-200 ease-out"
        enter-from-class="opacity-0 translate-y-2 scale-95"
        enter-to-class="opacity-100 translate-y-0 scale-100"
        leave-active-class="duration-150 ease-in"
        leave-from-class="opacity-100 translate-y-0 scale-100"
        leave-to-class="opacity-0 translate-y-2 scale-95"
      >
        <div
          v-if="purchaseResultOpen"
          class="relative"
        >
          <VipPurchaseResult
            :error-text="purchaseResultText"
            :incomplete-text="purchaseResultText"
            :show-close="true"
            :success-text="purchaseResultText"
            :type="purchaseResultType"
            @close="purchaseResultOpen = false"
          />
        </div>
      </transition>
    </div>
  </Teleport>
</template>

<style>
.tabsVIP {
  border-radius: 8px;
  margin-bottom: 8px;
}

.tabsVIP__tabs-navigation {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  cursor: grab;
  position: relative;
  user-select: none;
}

.tabsVIP__tabs-navigation::-webkit-scrollbar {
  display: none;
}

.tabsVIP__content {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  cursor: grab;
  position: relative;
}

.tabsVIP__content::-webkit-scrollbar {
  display: none;
}

.tabsVIP__cards-grid {
  display: flex;
  justify-content: flex-start;
  gap: 10px;
  padding-bottom: 8px;
}

.tabsVIP__card-wrapper {
  flex-shrink: 0;
}

.tabsVIP__card-wrapper.dragging {
  pointer-events: none;
}

/* Loading state */
.tabsVIP__loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: rgba(255, 255, 255, 0.5);
  gap: 12px;
}

.tabsVIP__loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top-color: #1414dd;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Empty state */
.tabsVIP__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: rgba(255, 255, 255, 0.5);
}

/* Desktop styles */
@media (min-width: 640px) {
  .tabsVIP {
    margin-bottom: 20px;
  }

  .tabsVIP__tabs-navigation {
    margin-bottom: 8px;
  }
}

/* Mobile: faz o swiper encostar na lateral direita */
@media (max-width: 414px) {
  .tabsVIP__tabs-navigation {
    margin-right: calc(-1 * var(--container-padding, 16px));
    padding-right: var(--container-padding, 16px);
  }

  .tabsVIP__content {
    margin-right: calc(-1 * var(--container-padding, 16px));
    padding-right: var(--container-padding, 16px);
  }
}

/* ========== SKELETON STYLES ========== */

/* Animação de pulse */
@keyframes tabsVIP-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}

/* ========== MISSION SKELETON (igual ao MissionCard) ========== */
.tabsVIP__skeleton-mission {
  width: 192px;
  height: 160px;
  background: #010612;
  border: 1px solid #1d2329;
  border-radius: 10px;
  padding: 5px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 6px;
  box-sizing: border-box;
}

.tabsVIP__skeleton-mission-header {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  flex-shrink: 0;
}

.tabsVIP__skeleton-mission-image {
  width: 52px;
  height: 52px;
  border-radius: 8px;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.08);
  animation: tabsVIP-pulse 1.5s ease-in-out infinite;
}

.tabsVIP__skeleton-mission-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.tabsVIP__skeleton-mission-title {
  width: 100%;
  height: 12px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.08);
  animation: tabsVIP-pulse 1.5s ease-in-out infinite;
  animation-delay: 0.1s;
}

.tabsVIP__skeleton-mission-title2 {
  width: 60%;
  height: 12px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.08);
  animation: tabsVIP-pulse 1.5s ease-in-out infinite;
  animation-delay: 0.15s;
}

.tabsVIP__skeleton-mission-desc {
  width: 100%;
  height: 10px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.08);
  animation: tabsVIP-pulse 1.5s ease-in-out infinite;
  animation-delay: 0.2s;
}

.tabsVIP__skeleton-mission-desc2 {
  width: 75%;
  height: 10px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.08);
  animation: tabsVIP-pulse 1.5s ease-in-out infinite;
  animation-delay: 0.25s;
}

/* Prêmio */
.tabsVIP__skeleton-mission-reward {
  width: 100%;
  height: 42px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.08);
  animation: tabsVIP-pulse 1.5s ease-in-out infinite;
  animation-delay: 0.3s;
  flex-shrink: 0;
}

/* Botão */
.tabsVIP__skeleton-mission-btn {
  width: 100%;
  height: 32px;
  border-radius: 4.5px;
  background: rgba(255, 255, 255, 0.08);
  animation: tabsVIP-pulse 1.5s ease-in-out infinite;
  animation-delay: 0.35s;
  flex-shrink: 0;
}

/* ========== TOURNAMENT SKELETON ========== */
.tabsVIP__skeleton-tournament {
  width: 250px;
  height: 160px;
  background: #010612;
  border: 1px solid #1d2329;
  border-radius: 10px;
  padding: 5px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-sizing: border-box;
}

.tabsVIP__skeleton-tournament-image {
  width: 100%;
  height: 70px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.08);
  animation: tabsVIP-pulse 1.5s ease-in-out infinite;
}

.tabsVIP__skeleton-tournament-title {
  width: 70%;
  height: 13px;
  margin: 4px auto 0;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.08);
  animation: tabsVIP-pulse 1.5s ease-in-out infinite;
  animation-delay: 0.1s;
}

.tabsVIP__skeleton-tournament-prizes {
  display: flex;
  justify-content: space-around;
  align-items: center;
  margin-top: 3px;
  gap: 6px;
}

.tabsVIP__skeleton-tournament-prize {
  width: 60px;
  height: 12px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.08);
  animation: tabsVIP-pulse 1.5s ease-in-out infinite;
  animation-delay: 0.15s;
}

.tabsVIP__skeleton-tournament-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  padding: 5px 6px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
}

.tabsVIP__skeleton-tournament-total {
  flex: 1;
  height: 24px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.08);
  animation: tabsVIP-pulse 1.5s ease-in-out infinite;
  animation-delay: 0.2s;
}

.tabsVIP__skeleton-tournament-btn {
  width: 80px;
  height: 30px;
  border-radius: 4.5px;
  background: rgba(255, 255, 255, 0.08);
  animation: tabsVIP-pulse 1.5s ease-in-out infinite;
  animation-delay: 0.25s;
}

/* ========== MINIGAME SKELETON ========== */
.tabsVIP__skeleton-minigame {
  width: 192px;
  height: 160px;
  background: #010612;
  border: 1px solid #1d2329;
  border-radius: 10px;
  padding: 5px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 6px;
  box-sizing: border-box;
}

.tabsVIP__skeleton-minigame-header {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.tabsVIP__skeleton-minigame-image {
  width: 52px;
  height: 52px;
  border-radius: 8px;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.08);
  animation: tabsVIP-pulse 1.5s ease-in-out infinite;
}

.tabsVIP__skeleton-minigame-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.tabsVIP__skeleton-minigame-title {
  width: 100%;
  height: 12px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.08);
  animation: tabsVIP-pulse 1.5s ease-in-out infinite;
  animation-delay: 0.1s;
}

.tabsVIP__skeleton-minigame-title2 {
  width: 60%;
  height: 12px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.08);
  animation: tabsVIP-pulse 1.5s ease-in-out infinite;
  animation-delay: 0.15s;
}

.tabsVIP__skeleton-minigame-desc {
  width: 100%;
  height: 10px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.08);
  animation: tabsVIP-pulse 1.5s ease-in-out infinite;
  animation-delay: 0.2s;
}

.tabsVIP__skeleton-minigame-desc2 {
  width: 75%;
  height: 10px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.08);
  animation: tabsVIP-pulse 1.5s ease-in-out infinite;
  animation-delay: 0.25s;
}

.tabsVIP__skeleton-minigame-price {
  width: 100%;
  height: 32px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.08);
  animation: tabsVIP-pulse 1.5s ease-in-out infinite;
  animation-delay: 0.3s;
}

.tabsVIP__skeleton-minigame-btn {
  width: 100%;
  height: 32px;
  border-radius: 4.5px;
  background: rgba(255, 255, 255, 0.08);
  animation: tabsVIP-pulse 1.5s ease-in-out infinite;
  animation-delay: 0.35s;
}

/* ========== STORE SKELETON ========== */
.tabsVIP__skeleton-store {
  width: 192px;
  height: 160px;
  background: #010612;
  border: 1px solid #1d2329;
  border-radius: 10px;
  padding: 5px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 6px;
  box-sizing: border-box;
}

.tabsVIP__skeleton-store-header {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.tabsVIP__skeleton-store-image {
  width: 52px;
  height: 52px;
  border-radius: 8px;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.08);
  animation: tabsVIP-pulse 1.5s ease-in-out infinite;
}

.tabsVIP__skeleton-store-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.tabsVIP__skeleton-store-title {
  width: 100%;
  height: 12px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.08);
  animation: tabsVIP-pulse 1.5s ease-in-out infinite;
  animation-delay: 0.1s;
}

.tabsVIP__skeleton-store-title2 {
  width: 60%;
  height: 12px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.08);
  animation: tabsVIP-pulse 1.5s ease-in-out infinite;
  animation-delay: 0.15s;
}

.tabsVIP__skeleton-store-desc {
  width: 100%;
  height: 10px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.08);
  animation: tabsVIP-pulse 1.5s ease-in-out infinite;
  animation-delay: 0.2s;
}

.tabsVIP__skeleton-store-desc2 {
  width: 75%;
  height: 10px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.08);
  animation: tabsVIP-pulse 1.5s ease-in-out infinite;
  animation-delay: 0.25s;
}

.tabsVIP__skeleton-store-price {
  width: 100%;
  height: 32px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.08);
  animation: tabsVIP-pulse 1.5s ease-in-out infinite;
  animation-delay: 0.3s;
}

.tabsVIP__skeleton-store-btn {
  width: 100%;
  height: 32px;
  border-radius: 4.5px;
  background: rgba(255, 255, 255, 0.08);
  animation: tabsVIP-pulse 1.5s ease-in-out infinite;
  animation-delay: 0.35s;
}
</style>
